import './UserAdmin.scss';
import * as R from 'ramda';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  GetUserAdminResponse,
  UpdateUserAdminParams,
  User
} from '@blisspointmedia/bpm-types/dist/UserAdmin';
import { Role, RoleType } from '@blisspointmedia/bpm-types/dist/Users';
import { Appearance, Intent } from '@Components/designConstants';
import { ModalEditWidget } from '@Components/ModalEditWidget';
import { SaveChangesInput, SelectorOption } from '@Components/ModalEditWidget/modal-edit-constants';
import { Page } from '@Components/Page';
import { FullPageSpinner, Spinner } from '@Components/Spinner';
import { Header } from '@Components/StickyTable/BPMTable';
import { PAGE_NAMES } from '@Components/Page/page-constants';
import { useSetError } from '../../redux/modals';
import * as UserRedux from '../../redux/user';
import { invalidEmailMessage, isInvalidOptionalEmail, isValidEmail } from '../../utils/email-utils';
import { awaitJSON, getSafeErrorMessage, UsersLambdaFetch } from '../../utils/fetch-utils';
import { Mixpanel, MxE } from '../../utils/mixpanelWrapper';
import { getCompanyOptions } from './user-admin-utils';
import { equalsIgnoreCase } from '../../utils/text-utils';

export const roleMap = {
  [Role.ADMIN]: 'Admin',
  [Role.INTERNAL]: 'User',
  [Role.EXTERNAL]: 'User'
};

// Helper function to determine role based on email address
const determineRoleFromEmail = (email: string, isAdmin = false): RoleType => {
  if (isAdmin) {
    return Role.ADMIN;
  }
  // Internal users have @tinuiti.com email addresses
  if (email && email.toLowerCase().endsWith('@tinuiti.com')) {
    return Role.INTERNAL;
  }
  return Role.EXTERNAL;
};

const INFO_TEXT_PARAGRAPHS = [
  'User Roles:',
  'Admin - P&T Privilege Only: Can access all pages for all clients.',
  'Bliss Point User - Access to all necessary pages within a given client.'
];

type UserWithTransformedRole = Omit<User, 'role' | 'override_role'> & {
  role: string | number;
  override_role: string | number;
};

const UserAdmin = () => {
  const setError = useSetError();
  const [allUsers, setAllUsers] = useState<User[]>();
  const [tableData, setTableData] = useState<User[]>();
  const [userData, setUserData] = useState<GetUserAdminResponse>();
  const [isSaving, setIsSaving] = useState(false);
  const role = useSelector(UserRedux.trueRoleSelector); // We want admins to be able to reset themselves so always use the actual role column not the override.
  const currentUserEmail = useSelector(UserRedux.emailSelector);
  const currentUserCompanies = useSelector(UserRedux.companiesSelector);
  const isAdmin = role === Role.ADMIN;

  const accessibleCompanyCids = useMemo(
    () => new Set(currentUserCompanies.map(c => c.company)),
    [currentUserCompanies]
  );

  const validateModalUserInput = (
    user: Partial<UserWithTransformedRole>,
    handleInvalidMessage?: (message: string) => void
  ): boolean => {
    if (!user.email) {
      handleInvalidMessage?.('Invalid Input: Email is required.');
      return false;
    }

    // Check shape before the duplicate lookup so a malformed address gets the specific complaint
    // rather than falling through to a less useful one.
    if (!isValidEmail(user.email)) {
      handleInvalidMessage?.(invalidEmailMessage(user.email));
      return false;
    }

    // Block adding a user whose email already belongs to another user. Match by a different id so
    // this doesn't fire when an existing user is simply editing their own row (its email is already
    // present in allUsers), but does fire for a new row or when retyping another user's email.
    const isDuplicateEmail = allUsers?.some(
      existing => equalsIgnoreCase(existing.email, user.email) && existing.id !== user.id
    );
    if (isDuplicateEmail) {
      handleInvalidMessage?.(
        'A user with this email already exists. To modify their access, find and select the existing user in the table.'
      );
      return false;
    }

    // Non-admins must not edit their own row — that would let them grant themselves access to
    // companies they shouldn't see. The Save button stays disabled while this is true so the
    // user gets a clear explanation but cannot persist the change.
    if (!isAdmin && user.email === currentUserEmail) {
      handleInvalidMessage?.(
        'You cannot edit your own user record. Please contact an administrator to change your access.'
      );
      return false;
    }

    const convertedUser = { ...user } as UserWithTransformedRole;

    if (convertedUser.role === 'Admin' && convertedUser.email.endsWith('@tinuiti.com')) {
      convertedUser.role = Role.ADMIN;
    } else {
      convertedUser.role = determineRoleFromEmail(convertedUser.email || '', false);
    }

    const roleElevationCheck = role === undefined || convertedUser.role >= role;
    if (!roleElevationCheck) {
      handleInvalidMessage?.(
        'Invalid Input: You do not have permissions to grant or modify users with privileges greater than your own'
      );
      return false;
    }

    if (!convertedUser.companies || convertedUser.companies.length === 0) {
      handleInvalidMessage?.(
        "Invalid Input: You need to assign a user to at least one company. If you want to assign a user to all companies, select 'All Companies' from the dropdown."
      );
      return false;
    }

    return true;
  };

  const getFreshUserData = useCallback(async () => {
    try {
      const res = await UsersLambdaFetch('/userAdmin/getUsers');
      const userDataLambdaResult = await awaitJSON<GetUserAdminResponse>(res);
      const usersWithRoles = userDataLambdaResult.users.map(user => ({
        ...user,
        role: roleMap[user.role],
        override_role: roleMap[user.override_role]
      }));

      setAllUsers(usersWithRoles);
      setTableData(usersWithRoles);
      setUserData(userDataLambdaResult);
    } catch (e) {
      setError({
        message: `Failed to get user admin data ${e.message}`,
        reportError: e
      });
    }
  }, [setError]);

  const userAdminHeader = useMemo(() => {
    let headers: Header[] = [
      {
        label: 'Email',
        field: 'email',
        type: 'text',
        // Optional-variant so a brand-new blank row isn't painted red the moment the modal opens;
        // blankness is still blocked by the required check in validateModalUserInput.
        isInvalid: isInvalidOptionalEmail,
        flex: 3,
        modalRow: 0,
        modalFlex: 1
      },
      {
        label: 'Companies',
        field: 'companies',
        type: 'select',
        options: 'company',
        isMulti: true,
        flex: 2,
        modalRow: 0,
        modalFlex: 1,
        renderer: (value: any) => {
          const { companies } = value;
          return <div className='companies-list'>{companies?.join(', ') || '-'}</div>;
        }
      }
    ];
    if (role === Role.ADMIN) {
      headers = R.concat(headers, [
        {
          label: 'Role',
          field: 'role',
          type: 'select',
          options: 'role',
          searchable: false,
          flex: 1,
          ...(role === Role.ADMIN && {
            modalRow: 1,
            modalFlex: 1,
            infoTextParagraphs: INFO_TEXT_PARAGRAPHS
          })
        },
        {
          label: 'Override Role',
          field: 'override_role',
          type: 'select',
          options: 'override_role',
          searchable: false,
          flex: 1,
          modalRow: 1,
          modalFlex: 1,
          infoTextParagraphs: INFO_TEXT_PARAGRAPHS
        },
        {
          label: 'Send Linear Buys?',
          field: 'send_linear_buys',
          type: 'checkbox',
          flex: 1,
          modalRow: 1,
          modalFlex: 1
        },
        {
          label: 'Full Name',
          field: 'full_name',
          type: 'text',
          flex: 2,
          modalRow: 1,
          modalFlex: 1
        }
      ]);
    }

    return R.filter(elem => {
      const { field } = elem;
      return (
        role === Role.ADMIN ||
        (field !== 'send_linear_buys' && field !== 'full_name' && field !== 'override_role')
      );
    }, headers);
  }, [role]);

  useEffect(() => {
    if (!navigator.cookieEnabled) {
      const error: Error = {
        name: '500',
        message: 'You need to have cookies enabled to use this page!'
      };
      setError({
        message: error.message,
        reportError: error
      });
    }

    if (!allUsers) {
      (async () => {
        await getFreshUserData();
      })();
    }
  }, [setError, allUsers, getFreshUserData]);

  const selectorOptions: Record<string, SelectorOption[]> = useMemo(() => {
    if (userData) {
      // For admins, show Admin and User options
      // For non-admins, role will be auto-determined from email, so no options needed
      const roleOptions =
        role === Role.ADMIN
          ? [
              { label: 'Admin', value: 'Admin' },
              { label: 'User', value: 'User' }
            ]
          : [];

      return {
        role: roleOptions,
        override_role: roleOptions,
        company: getCompanyOptions(userData.companies, accessibleCompanyCids, userData.callerHasAll)
      };
    }
    return { role: [], override_role: [], company: [] };
  }, [userData, role, accessibleCompanyCids]);

  const cleanUpUser = useCallback(
    (user: UserWithTransformedRole) => {
      const convertedUser = { ...user } as any;

      // Determine the actual role based on email and admin selection
      let actualRole: RoleType;

      if (role === Role.ADMIN) {
        // Admin user is editing
        if (convertedUser.role === 'Admin' || convertedUser.role === Role.ADMIN) {
          actualRole = Role.ADMIN;
        } else {
          // "External User" or any other value - auto-determine from email
          actualRole = determineRoleFromEmail(convertedUser.email || '', false);
        }
      } else {
        // Non-admin user - always auto-determine from email
        actualRole = determineRoleFromEmail(convertedUser.email || '', false);
      }

      convertedUser.role = actualRole;

      // Handle override_role
      if (role === Role.ADMIN) {
        // Admin can set override role
        if (convertedUser.override_role === 'Admin' || convertedUser.override_role === Role.ADMIN) {
          convertedUser.override_role = Role.ADMIN;
        } else if (
          convertedUser.override_role === 'User' ||
          convertedUser.override_role === Role.INTERNAL ||
          convertedUser.override_role === Role.EXTERNAL
        ) {
          // Auto-determine from email for non-admin override
          convertedUser.override_role = determineRoleFromEmail(convertedUser.email || '', false);
        }
      } else {
        // Non-admin - set override_role same as role
        convertedUser.override_role = actualRole;
      }

      if (convertedUser.companies && convertedUser.companies.includes('all')) {
        convertedUser.companies = ['all'];
      }
      return convertedUser;
    },
    [role]
  );

  const saveAllChanges = useCallback(
    async ({
      modifiedData,
      addedData,
      deletedData,
      cleanup
    }: SaveChangesInput<UserWithTransformedRole>) => {
      setIsSaving(true);
      try {
        const inserts: User[] = addedData.map(cleanUpUser);
        const updates: User[] = modifiedData.map(cleanUpUser);
        const deletes: User[] = deletedData.map(cleanUpUser);

        await UsersLambdaFetch<UpdateUserAdminParams>('/userAdmin/updateUsers', {
          method: 'POST',
          body: { insert: inserts, update: updates, delete: deletes }
        });

        if (inserts.length) {
          Mixpanel.track(MxE.RecordCreated, { page: PAGE_NAMES.USER_ADMIN, entity: 'user' });
        }
        if (updates.length) {
          Mixpanel.track(MxE.RecordSaved, { page: PAGE_NAMES.USER_ADMIN, entity: 'user' });
        }
        if (deletes.length) {
          Mixpanel.track(MxE.RecordDeleted, { page: PAGE_NAMES.USER_ADMIN, entity: 'user' });
        }

        await getFreshUserData();
        cleanup();
      } catch (e) {
        setError({ message: `Failed to save changes: ${e.message}`, reportError: e });
      } finally {
        setIsSaving(false);
      }
    },
    [cleanUpUser, getFreshUserData, setError]
  );

  const resendInvitation = useCallback(
    async (data: UserWithTransformedRole) => {
      if (data.email) {
        try {
          const res = await UsersLambdaFetch('/resendInvitation', {
            method: 'POST',
            body: {
              email: data.email
            }
          });
          if (res.status === 200) {
            setError({
              title: 'Success',
              intent: Intent.SUCCESS,
              message: 'Invitation was resent!'
            });
            Mixpanel.track(MxE.ActionTriggered, {
              page: PAGE_NAMES.USER_ADMIN,
              action: 'resend_invitation'
            });
          }
        } catch (e) {
          setError({
            message: getSafeErrorMessage(e),
            reportError: e
          });
        }
      } else {
        const error = new Error('Do not have a valid email to resend invitation to.');
        setError({
          message: error.message,
          reportError: error
        });
      }
    },
    [setError]
  );

  const resendInvitationButton = {
    label: 'Resend Invitation',
    variant: Appearance.FILLED,
    onClick: resendInvitation,
    showWhen: 'editing' as const
  };

  const savingOverlay = (
    <div className='loading-overlay'>
      <div className='loading-spinner'>
        <Spinner size={40} />
        <div className='loading-text'>Saving...</div>
      </div>
    </div>
  );

  return (
    <Page title={PAGE_NAMES.USER_ADMIN} pageType={PAGE_NAMES.USER_ADMIN}>
      <div className='user-admin-page-container'>
        {isSaving && savingOverlay}
        {tableData ? (
          <ModalEditWidget<UserWithTransformedRole>
            className='user-admin-table'
            name='Users'
            addButtonText='Add User'
            deleteButtonText='Delete User'
            headers={userAdminHeader}
            initialTableData={tableData}
            selectorOptions={selectorOptions}
            filterBar
            checkIsValid={validateModalUserInput}
            defaultNewRow={{ role: role === Role.ADMIN ? 'User' : '' }}
            customModalFooterButtons={[resendInvitationButton]}
            onSaveModal={saveAllChanges}
            dataTestId='userAdminTable'
          />
        ) : (
          <FullPageSpinner />
        )}
      </div>
    </Page>
  );
};

export { UserAdmin };
